import {
  Injectable,
  TemplateRef,
  Type,
  Injector,
  ComponentFactoryResolver,
  Inject,
} from "@angular/core";
import {
  NbDialogConfig,
  NbOverlayRef,
  NbGlobalPositionStrategy,
  NbPositionBuilderService,
  NbScrollStrategy,
  NbDialogRef,
  NbTemplatePortal,
  NbComponentPortal,
  NbPortalInjector,
  NbOverlayService,
  NB_DOCUMENT,
  NB_DIALOG_CONFIG,
  NbDialogContainerComponent,
} from "@nebular/theme";
import { fromEvent as observableFromEvent } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
@Injectable({
  providedIn: "root",
})
export class ScaffoldSidebarService {
  private _dialogRef;
  get dialogRef() {
    return this._dialogRef;
  }
  set dialogRef(dialogRef) {
    this._dialogRef = dialogRef;
  }
  constructor(
    @Inject(NB_DOCUMENT) protected document,
    @Inject(NB_DIALOG_CONFIG) protected globalConfig,
    protected positionBuilder: NbPositionBuilderService,
    protected overlay: NbOverlayService,
    protected injector: Injector,
    protected cfr: ComponentFactoryResolver
  ) {}

  open<T>(
    content: Type<T> | TemplateRef<T>,
    userConfig: Partial<NbDialogConfig<Partial<T> | string>> = {}
  ): NbDialogRef<T> {
    const config = new NbDialogConfig({
      ...{ hasBackdrop: false, closeOnBackdropClick: false, hasScroll: true },
      ...userConfig,
    });
    const overlayRef = this.createOverlay(config);
    const dialogRef = new NbDialogRef<T>(overlayRef);
    const container = this.createContainer(config, overlayRef);
    this.createContent(config, content, container, dialogRef);

    this.registerCloseListeners(config, overlayRef, dialogRef);
    this.dialogRef = dialogRef;
    return dialogRef;
  }

  protected createOverlay(config: NbDialogConfig): NbOverlayRef {
    const positionStrategy = this.createPositionStrategy();
    const scrollStrategy = this.createScrollStrategy(config.hasScroll);

    return this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: config.hasBackdrop,
      panelClass: config.dialogClass,
    });
  }

  protected createPositionStrategy(): NbGlobalPositionStrategy {
    return this.positionBuilder.global().right();
  }

  protected createScrollStrategy(hasScroll: boolean): NbScrollStrategy {
    if (hasScroll) {
      return this.overlay.scrollStrategies.noop();
    } else {
      return this.overlay.scrollStrategies.block();
    }
  }

  protected createContainer(
    config: NbDialogConfig,
    overlayRef: NbOverlayRef
  ): NbDialogContainerComponent {
    const injector = new NbPortalInjector(
      this.createInjector(config),
      new WeakMap([[NbDialogConfig, config]])
    );
    const containerPortal = new NbComponentPortal(
      NbDialogContainerComponent,
      null,
      injector,
      this.cfr
    );
    const containerRef = overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  protected createContent<T>(
    config: NbDialogConfig,
    content: Type<T> | TemplateRef<T>,
    container: NbDialogContainerComponent,
    dialogRef: NbDialogRef<T>
  ) {
    if (content instanceof TemplateRef) {
      const portal = this.createTemplatePortal(config, content, dialogRef);
      container.attachTemplatePortal(portal);
    } else {
      const portal = this.createComponentPortal(config, content, dialogRef);
      dialogRef.componentRef = container.attachComponentPortal(portal);

      if (config.context) {
        Object.assign(dialogRef.componentRef.instance, { ...config.context });
      }
    }
  }

  protected createTemplatePortal<T>(
    config: NbDialogConfig,
    content: TemplateRef<T>,
    dialogRef: NbDialogRef<T>
  ): NbTemplatePortal {
    return new NbTemplatePortal(content, null, <any>{
      $implicit: config.context,
      dialogRef,
    });
  }

  /**
   * We're creating portal with custom injector provided through config or using global injector.
   * This approach provides us capability inject `NbDialogRef` in dialog component.
   * */
  protected createComponentPortal<T>(
    config: NbDialogConfig,
    content: Type<T>,
    dialogRef: NbDialogRef<T>
  ): NbComponentPortal {
    const injector = this.createInjector(config);
    const portalInjector = new NbPortalInjector(
      injector,
      new WeakMap([[NbDialogRef, dialogRef]])
    );
    return new NbComponentPortal(
      content,
      config.viewContainerRef,
      portalInjector
    );
  }

  protected createInjector(config: NbDialogConfig): Injector {
    return (
      (config.viewContainerRef && config.viewContainerRef.injector) ||
      this.injector
    );
  }

  protected registerCloseListeners<T>(
    config: NbDialogConfig,
    overlayRef: NbOverlayRef,
    dialogRef: NbDialogRef<T>
  ) {
    if (config.closeOnBackdropClick) {
      overlayRef.backdropClick().subscribe(() => dialogRef.close());
    }

    if (config.closeOnEsc) {
      observableFromEvent(this.document, "keyup")
        .pipe(
          filter((event: KeyboardEvent) => event.keyCode === 27),
          takeUntil(dialogRef.onClose)
        )
        .subscribe(() => dialogRef.close());
    }
  }
}
